import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  Pencil,
  Search,
  Trash2,
  Plus,
  Package,
  FileSpreadsheet,
  X,
} from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import { productsService } from "../../../services/products.service";
import { env } from "../../../config/env";
import { getAuthToken } from "../../AdminAuth/components/authStore";
import ImportProductsModal from "./ImportProductsModal";

import CatalogManagerDrawer from "./CatalogManagerDrawer";
import ThemeMessagePopup from "../Products/component/ThemeMessagePopup";

function SelectLike({
  value,
  onChange,
  placeholder,
  options,
  disabled = false,
}) {
  const [open, setOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selected = options.find((o) => (o.value ?? o) === value);

  return (
    <div ref={dropdownRef} className="relative min-w-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        className={`h-11 w-full rounded-xl border px-4 pr-10 text-left text-[13px] outline-none transition
          ${disabled
            ? "cursor-not-allowed border-[#f2c6cc] bg-slate-50 text-slate-400"
            : "border-[#f2c6cc] bg-white text-slate-600 hover:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
          }`}
      >
        {selected ? selected.label ?? selected : placeholder}
      </button>

      <ChevronDown
        size={18}
        className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition ${open ? "rotate-180" : ""
          }`}
      />

      {open && !disabled && (
        <div className="absolute z-50 mt-2 max-h-64 w-full overflow-y-auto rounded-xl border border-[#f2c6cc] bg-white shadow-xl">
          <button
            type="button"
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className="w-full px-4 py-3 text-left text-[13px] text-slate-500 hover:bg-[#fff4f6] hover:text-[#d86a78]"
          >
            {placeholder}
          </button>

          {options.map((o) => {
            const val = o.value ?? o;
            const label = o.label ?? o;
            const active = value === val;

            return (
              <button
                key={val}
                type="button"
                onClick={() => {
                  onChange(val);
                  setOpen(false);
                }}
                className={`w-full px-4 py-3 text-left text-[13px] transition
                  ${active
                    ? "bg-[#d86a78] text-white"
                    : "text-slate-600 hover:bg-[#fff4f6] hover:text-[#d86a78]"
                  }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function toImageUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = env?.CATALOG_BASE_URL || process.env.REACT_APP_CATALOG_BASE_URL || "";
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
}

function StatusPill({ value }) {
  const isActive = String(value).toLowerCase() === "active";
  return (
    <span
      className={
        "inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold capitalize " +
        (isActive ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600")
      }
    >
      {value}
    </span>
  );
}

function ActiveDot({ active }) {
  const ok = Boolean(active);
  return (
    <span
      className={
        "inline-flex shrink-0 items-center gap-2 text-[12px] font-semibold " +
        (ok ? "text-emerald-600" : "text-rose-600")
      }
    >
      <span className={"h-2 w-2 rounded-full " + (ok ? "bg-emerald-500" : "bg-rose-500")} />
      {ok ? "Yes" : "No"}
    </span>
  );
}

function Switch({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={
        "relative h-[22px] w-[48px] shrink-0 overflow-hidden rounded-full transition focus:outline-none focus:ring-2 focus:ring-[#f7d7db] focus:ring-offset-1 " +
        (disabled ? "cursor-not-allowed opacity-60 " : "") +
        (checked ? "bg-[#d86a78]" : "bg-slate-300")
      }
    >
      <span
        className={
          "absolute top-[3px] h-4 w-4 rounded-full bg-white shadow transition " +
          (checked ? "left-[29px]" : "left-[3px]")
        }
      />
    </button>
  );
}

function normalizeList(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.items)) return json.items;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.rows)) return json.rows;
  if (Array.isArray(json?.result)) return json.result;
  return [];
}

async function fetchAdminList(path, opts = {}) {
  const base = env?.CATALOG_BASE_URL || process.env.REACT_APP_CATALOG_BASE_URL || "";
  const token = getAuthToken?.() || "";

  const res = await fetch(`${base}${path}`, {
    method: opts.method || "GET",
    headers: {
      accept: "application/json",
      ...(opts.body ? { "Content-Type": "application/json" } : {}),
      Authorization: token ? `Bearer ${token}` : "",
      ...(opts.headers || {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  const text = await res.text();
  let json = {};
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    json = { message: text || "Invalid JSON" };
  }

  if (!res.ok) {
    const msg = json?.message || `Request failed (${res.status})`;
    throw new Error(msg);
  }

  return json;
}

function AddProductChoiceModal({ open, onClose, onManual, onImport }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-end justify-center bg-black/45 p-3 sm:items-center sm:p-4">
      <div className="relative flex max-h-[90vh] w-full max-w-[760px] flex-col overflow-hidden rounded-[22px] bg-white shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 sm:right-4 sm:top-4"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="border-b border-[#f4d9dd] px-4 py-5 text-center sm:px-8 sm:py-6">
          <h2 className="pr-8 text-[22px] font-bold text-[#d86a78] sm:pr-0 sm:text-[28px]">Add Product</h2>
          <p className="mt-2 text-[13px] text-slate-500 sm:text-[14px]">
            Choose how you want to add your product to the system.
          </p>
        </div>

        <div className="overflow-y-auto px-4 py-5 sm:px-8 sm:py-7">
          <div className="grid grid-cols-1 gap-4">
            <button
              type="button"
              onClick={onManual}
              className="group rounded-[18px] border border-[#f2c6cc] bg-[#fff8f9] p-5 text-left transition hover:-translate-y-[2px] hover:border-[#d86a78] hover:shadow-lg sm:p-6"
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-[16px] bg-[#fdecef] text-[#d86a78] sm:h-14 sm:w-14">
                <Package size={26} />
              </div>

              <h3 className="text-[18px] font-bold text-slate-800 sm:text-[22px]">Add Product Manually</h3>
              <p className="mt-2 text-[13px] leading-6 text-slate-500 sm:text-[14px]">
                Create a single new product profile manually with all details, pricing, image and stock information.
              </p>

              <div className="mt-5 inline-flex items-center rounded-[10px] bg-[#d86a78] px-4 py-2 text-[13px] font-semibold text-white transition group-hover:bg-[#c75a69]">
                Continue
              </div>
            </button>

            <button
              type="button"
              onClick={onImport}
              className="group rounded-[18px] border border-[#f2c6cc] bg-white p-5 text-left transition hover:-translate-y-[2px] hover:border-[#d86a78] hover:shadow-lg sm:p-6"
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-[16px] bg-[#fdecef] text-[#d86a78] sm:h-14 sm:w-14">
                <FileSpreadsheet size={26} />
              </div>

              <h3 className="text-[18px] font-bold text-slate-800 sm:text-[22px]">Import Excel</h3>
              <p className="mt-2 text-[13px] leading-6 text-slate-500 sm:text-[14px]">
                Upload multiple products at once using an Excel or CSV file and quickly add them in bulk.
              </p>

              <div className="mt-5 inline-flex items-center rounded-[10px] border border-[#f2c6cc] px-4 py-2 text-[13px] font-semibold text-[#d86a78] transition group-hover:bg-[#fff4f6]">
                Import Now
              </div>
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-[#f4d9dd] bg-[#fffafb] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div className="text-center text-[13px] text-slate-400 sm:text-left">Choose one option to continue</div>
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-[10px] border border-[#f2c6cc] px-5 py-2 text-[13px] font-semibold text-[#d86a78] transition hover:bg-[#fff1f3] sm:w-auto"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 break-words text-[13px] font-medium text-slate-700">{value || "-"}</div>
    </div>
  );
}

function ProductCard({ product, switching, onView, onEdit, onDelete, onToggle }) {
  return (
    <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onView}
          className="overflow-hidden rounded-[12px] border border-slate-200 bg-slate-50"
        >
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              className="h-16 w-16 object-cover sm:h-[72px] sm:w-[72px]"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center text-slate-300 sm:h-[72px] sm:w-[72px]">
              <Package size={24} />
            </div>
          )}
        </button>

        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={onView}
            className="block text-left text-[15px] font-bold text-slate-800 transition hover:text-[#d86a78]"
          >
            {product.name}
          </button>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusPill value={product.status} />
            <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-500">
              SKU: {product.sku}
            </span>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <ActiveDot active={product.active} />
            <Switch checked={Boolean(product.active)} disabled={switching} onChange={onToggle} />
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Barcode" value={product.barcode} />
        <Field label="Category" value={product.category} />
        <Field label="Brand" value={product.brand} />
        <Field label="Unit" value={product.unit} />
        <Field label="Tax" value={product.tax} />
        <Field label="Weight" value={product.weight} />
        <Field label="MRP" value={product.mrp} />
        <Field label="Selling Price" value={product.sellingPrice} />
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#f2c6cc] px-4 py-2.5 text-[13px] font-semibold text-[#d86a78] transition hover:bg-[#fff4f6]"
        >
          <Pencil size={16} />
          Edit
        </button>

        <button
          type="button"
          onClick={onDelete}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-rose-200 px-4 py-2.5 text-[13px] font-semibold text-rose-700 transition hover:bg-rose-50"
        >
          <Trash2 size={16} />
          Delete
        </button>
      </div>
    </div>
  );
}

export default function ProductsList() {
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const getPositiveNumber = (value, fallback) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };

  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [status, setStatus] = useState(() => searchParams.get("status") || "");
  const [category, setCategory] = useState(() => searchParams.get("category") || "");
  const [subcategory, setSubcategory] = useState(() => searchParams.get("subcategory") || "");
  const [brand, setBrand] = useState(() => searchParams.get("brand") || "");

  const [page, setPage] = useState(() => getPositiveNumber(searchParams.get("page"), 1));
  const [pageSize, setPageSize] = useState(() => getPositiveNumber(searchParams.get("limit"), 10));

  const productListReturnTo = `${location.pathname}${location.search || ""}`;

  const openProductDetails = (productId) => {
    nav(`/admin/dashboard/products/${productId}`, {
      state: { returnTo: productListReturnTo },
    });
  };

  const openProductEdit = (productId) => {
    nav(`/admin/dashboard/products/${productId}/edit`, {
      state: { returnTo: productListReturnTo },
    });
  };

  const [popup, setPopup] = useState({ open: false, row: null });
  const [mutating, setMutating] = useState({ deleting: false, activeIds: new Set() });
  const [messagePopup, setMessagePopup] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });

  const [apiState, setApiState] = useState({
    loading: false,
    error: "",
    items: [],
    total: 0,
  });

  const [lookups, setLookups] = useState({
    loading: false,
    error: "",
    categories: [],
    brands: [],
    units: [],
    categoryMap: {},
    brandMap: {},
    unitMap: {},
  });

  function showMessage(type, title, message) {
    setMessagePopup({
      open: true,
      type,
      title,
      message,
    });

    window.clearTimeout(showMessage._timer);
    showMessage._timer = window.setTimeout(() => {
      setMessagePopup((prev) => ({ ...prev, open: false }));
    }, 3000);
  }

  const [importModal, setImportModal] = useState(false);
  const [addChoiceModal, setAddChoiceModal] = useState(false);
  const [openCatalog, setOpenCatalog] = useState(false);

  async function loadProducts(
    nextPage = page,
    nextPageSize = pageSize,
    nextQ = q,
    nextFilters = { status, category, subcategory, brand }
  ) {
    try {
      setApiState((s) => ({ ...s, loading: true, error: "" }));

      const res = await productsService.listProducts({
        page: nextPage,
        limit: nextPageSize,
        q: nextQ?.trim() || "",
        status: nextFilters.status || "",
        categoryId: nextFilters.subcategory || nextFilters.category || "",
        subcategoryId: "",
        brandId: nextFilters.brand || "",
      });

      const items = Array.isArray(res?.items)
        ? res.items
        : Array.isArray(res?.data)
          ? res.data
          : [];

      const total = Number(res?.total || 0);

      setApiState({ loading: false, error: "", items, total });
    } catch (e) {
      setApiState({
        loading: false,
        error: e?.message || "Failed to load products",
        items: [],
        total: 0,
      });
    }
  }

  useEffect(() => {
    const params = new URLSearchParams();

    if (page > 1) params.set("page", String(page));
    if (pageSize !== 10) params.set("limit", String(pageSize));
    if (q.trim()) params.set("q", q.trim());
    if (status) params.set("status", status);
    if (category) params.set("category", category);
    if (subcategory) params.set("subcategory", subcategory);
    if (brand) params.set("brand", brand);

    setSearchParams(params, { replace: true });
  }, [page, pageSize, q, status, category, subcategory, brand, setSearchParams]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLookups((s) => ({ ...s, loading: true, error: "" }));

        const [catsJson, brandsJson, unitsJson] = await Promise.all([
          fetchAdminList("/api/admin/categories/"),
          fetchAdminList("/api/admin/brands/"),
          fetchAdminList("/api/admin/units/"),
        ]);

        const cats = normalizeList(catsJson);
        const brands = normalizeList(brandsJson);
        const units = normalizeList(unitsJson);

        const categoryMap = {};
        for (const c of cats) if (c?.id) categoryMap[c.id] = c.name || c.slug || c.id;

        const brandMap = {};
        for (const b of brands) if (b?.id) brandMap[b.id] = b.name || b.slug || b.id;

        const unitMap = {};
        for (const u of units) if (u?.id) unitMap[u.id] = u.symbol || u.name || u.id;

        if (!alive) return;

        setLookups({
          loading: false,
          error: "",
          categories: cats,
          brands,
          units,
          categoryMap,
          brandMap,
          unitMap,
        });
      } catch (e) {
        if (!alive) return;
        setLookups((s) => ({
          ...s,
          loading: false,
          error: e?.message || "Failed to load categories/brands/units",
        }));
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!alive) return;
      await loadProducts(page, pageSize, q, { status, category, subcategory, brand });
    })();

    return () => {
      alive = false;
    };
  }, [page, pageSize, q, status, category, subcategory, brand]);

  const pageSizeOptions = useMemo(() => {
    const total = Number(apiState.total || 0);
    const base = [10, 25, 50, 100, 250, 500];

    let opts = base.filter((n) => total > 0 && n <= total);

    if (total > 0 && !opts.includes(total)) opts.push(total);
    if (total <= 0) opts = [10, 25, 50, 100, 250, 500];

    return Array.from(new Set(opts)).sort((a, b) => a - b);
  }, [apiState.total]);

  const onChangePageSize = (v) => {
    const n = Number(v);
    setPage(1);
    setPageSize(Number.isFinite(n) && n > 0 ? n : 10);
  };

  const mapped = useMemo(() => {
    const { categoryMap, brandMap, unitMap } = lookups;

    const base = (apiState.items || []).map((p) => {
      const categoryId = p.categoryId ?? p.category_id ?? "";
      const brandId = p.brandId ?? p.brand_id ?? "";
      const unitId = p.unitId ?? p.unit_id ?? "";

      const taxPercent = p.taxPercent ?? p.tax_percent ?? null;
      const weightValue =
        p.weightValue ??
        p.weight_value ??
        p.defaultWeightValue ??
        p.default_weight_value ??
        p.defaultVariant?.weightValue ??
        p.defaultVariant?.weight_value ??
        null;

      const weightUnitSymbol =
        p.weightUnitSymbol ??
        p.weight_unit_symbol ??
        p.defaultWeightUnitSymbol ??
        p.default_weight_unit_symbol ??
        p.defaultVariant?.weightUnitSymbol ??
        p.defaultVariant?.weight_unit_symbol ??
        "";

      const cleanWeightValue =
        weightValue === null || weightValue === undefined || String(weightValue).trim() === ""
          ? ""
          : String(weightValue).trim();

      const cleanWeightUnit = String(weightUnitSymbol || "").trim();

      const displayWeightValue = Number.isFinite(Number(cleanWeightValue))
        ? Number(cleanWeightValue).toString()
        : cleanWeightValue;

      const weight = displayWeightValue ? `${displayWeightValue} ${cleanWeightUnit}`.trim() : "-";

      return {
        id: p.id,
        status: p.status || "-",
        active: p.isActive ?? p.is_active ?? false,
        barcode: p.barcode ?? p.bar_code ?? "-",
        sku: p.sku || "-",
        name: p.name || "-",
        categoryId,
        brandId,
        unitId,
        category: p.categoryName || (categoryId ? categoryMap?.[categoryId] || "-" : "-"),
        brand: p.brandName || (brandId ? brandMap?.[brandId] || "-" : "-"),
        unit: p.unitSymbol || p.unitName || (unitId ? unitMap?.[unitId] || "-" : "-"),
        tax: taxPercent == null ? "-" : `${taxPercent}%`,
        weight,
        image: toImageUrl(p.imageUrl || p.image_url || p.primary_image_url || ""),
        mrp: p.mrp ?? "-",
        sellingPrice: p.sellingPrice ?? "-",
        // purchasePrice:
        //   p.purchasePrice === null || p.purchasePrice === undefined || p.purchasePrice === ""
        //     ? "-"
        //     : `₹${Number(p.purchasePrice).toFixed(2)}`,
        // _raw: p,
      };
    });

    return base;
  }, [apiState.items, lookups]);

  const options = useMemo(() => {
    const categories = lookups.categories || [];
    const byId = new Map(categories.map((item) => [String(item?.id || ""), item]));
    const categoryOptions = categories
      .filter((item) => item?.id)
      .map((item) => {
        const path = [];
        const seen = new Set();
        let current = item;
        while (current?.id && !seen.has(String(current.id))) {
          seen.add(String(current.id));
          path.unshift(current?.name || current?.slug || current?.id);
          const parentId = current?.parentId ?? current?.parent_id ?? null;
          current = parentId ? byId.get(String(parentId)) : null;
        }
        return { value: item.id, label: path.filter(Boolean).join(" > ") };
      })
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));

    const brandOptions = Object.entries(lookups.brandMap || {})
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));

    return {
      status: [
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
      ],
      category: categoryOptions,
      brand: brandOptions,
    };
  }, [lookups]);


  const totalPages = useMemo(() => {
    const total = Number(apiState.total || 0);
    if (!total || total <= 0) return 1;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [apiState.total, pageSize]);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  const total = Number(apiState.total || 0);
  const startIndex = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIndex = total === 0 ? 0 : Math.min(page * pageSize, total);

  function askDelete(row) {
    setPopup({ open: true, row });
  }

  async function handleToggleActive(row, nextValue) {
    if (!row?.id) return;

    try {
      setMutating((prev) => ({
        ...prev,
        activeIds: new Set([...prev.activeIds, row.id]),
      }));

      await productsService.setProductActive(row.id, nextValue);

      setApiState((prev) => ({
        ...prev,
        items: (prev.items || []).map((item) =>
          item.id === row.id
            ? {
              ...item,
              isActive: nextValue,
              is_active: nextValue,
              status: nextValue ? "active" : "inactive",
            }
            : item
        ),
      }));

      showMessage(
        nextValue ? "success" : "info",
        nextValue ? "Product Activated" : "Product Deactivated",
        nextValue
          ? `${row?.name || "Product"} has been activated successfully.`
          : `${row?.name || "Product"} has been deactivated successfully.`
      );
    } catch (e) {
      showMessage("error", "Status Update Failed", e?.message || "Failed to update product status.");
    } finally {
      setMutating((prev) => {
        const next = new Set(prev.activeIds);
        next.delete(row.id);
        return {
          ...prev,
          activeIds: next,
        };
      });
    }
  }

  return (
    <div className="w-full max-w-full overflow-hidden px-0">
      <ConfirmModal
        open={popup.open}
        icon="trash"
        variant="confirm"
        title={`Are you Sure You want to Delete ${popup.row?.name || ""}`}
        description="This will remove this product from the system. Would you like to Proceed?"
        onClose={() => {
          if (mutating.deleting) return;
          setPopup({ open: false, row: null });
        }}
        onConfirm={async () => {
          if (!popup.row?.id) return setPopup({ open: false, row: null });

          try {
            setMutating((s) => ({ ...s, deleting: true }));

            const res = await productsService.deleteProduct(popup.row.id);

            if (res?.mode === "soft") {
              setApiState((s) => ({
                ...s,
                items: (s.items || []).map((it) =>
                  String(it.id) === String(popup.row.id)
                    ? {
                      ...it,
                      isActive: false,
                      is_active: false,
                      status: "inactive",
                    }
                    : it
                ),
              }));

              showMessage(
                "info",
                "Product Deactivated",
                res?.message || "Product is used somewhere so it is deactivated."
              );
            } else {
              setApiState((s) => ({
                ...s,
                items: (s.items || []).filter((it) => String(it.id) !== String(popup.row.id)),
                total: Math.max(0, Number(s.total || 0) - 1),
              }));

              showMessage("success", "Product Deleted", res?.message || "Product deleted successfully.");
            }

            setPopup({ open: false, row: null });
          } catch (e) {
            showMessage("error", "Delete Failed", e?.message || "Something went wrong.");
          } finally {
            setMutating((s) => ({ ...s, deleting: false }));
          }
        }}
        cancelText="NO"
        confirmText={mutating.deleting ? "Deleting…" : "YES"}
      />

      <ThemeMessagePopup
        open={messagePopup.open}
        type={messagePopup.type}
        title={messagePopup.title}
        message={messagePopup.message}
        onClose={() => setMessagePopup((prev) => ({ ...prev, open: false }))}
      />

      <AddProductChoiceModal
        open={addChoiceModal}
        onClose={() => setAddChoiceModal(false)}
        onManual={() => {
          setAddChoiceModal(false);
          nav("/admin/dashboard/products/new");
        }}
        onImport={() => {
          setAddChoiceModal(false);
          setImportModal(true);
        }}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-[22px] font-bold tracking-wide text-[#d86a78] sm:text-[25px]">PRODUCTS</h1>
          <p className="mt-1 text-[13px] text-slate-500">
            Manage products, filters, pricing view, and status in one place.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setAddChoiceModal(true)}
          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#d86a78] px-5 text-[13px] font-semibold text-white transition hover:bg-[#c95b69] sm:w-auto"
        >
          <Plus size={16} />
          Add Product
        </button>
      </div>

      <div className="mt-5 rounded-[20px] border border-[#f2c6cc] bg-transparent p-3 sm:p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(300px,1.7fr)_repeat(3,minmax(170px,1fr))_auto] xl:items-center">
          <div className="relative min-w-0 sm:col-span-2 xl:col-span-1">
            <input
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder="Search by product name, SKU or barcode"
              className="h-11 w-full rounded-xl border border-[#f2c6cc] bg-white pl-11 pr-4 text-[13px] text-slate-600 outline-none transition focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
            />
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300" />
          </div>

          <SelectLike
            value={status}
            onChange={(v) => {
              setPage(1);
              setStatus(v);
            }}
            placeholder="Status"
            options={options.status}
          />

          <SelectLike
            value={subcategory || category}
            onChange={(v) => {
              setPage(1);
              setCategory(v);
              setSubcategory("");
            }}
            placeholder="Any category level"
            options={options.category}
          />

          <SelectLike
            value={brand}
            onChange={(v) => {
              setPage(1);
              setBrand(v);
            }}
            placeholder="Brand"
            options={options.brand}
          />

          <button
            type="button"
            onClick={() => setOpenCatalog(true)}
            className="inline-flex h-11 w-full items-center justify-center rounded-full border border-[#f2c6cc] bg-white px-5 text-[13px] font-semibold text-[#d86a78] shadow-sm transition hover:bg-[#fff7f8] xl:w-auto"
          >
            Manage Catalog
          </button>
        </div>
      </div>

      {(apiState.error || lookups.error) && (
        <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">
          {apiState.error || lookups.error}
        </div>
      )}

      <div className="mt-5 hidden overflow-hidden rounded-[18px] border border-[#f2c6cc] bg-white 2xl:block">
        <div className="w-full overflow-hidden">
          <table className="w-full table-fixed [&_td]:align-middle">
            <colgroup>
              <col style={{ width: "4.5%" }} />
              <col style={{ width: "14.5%" }} />
              <col style={{ width: "10.5%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "6.5%" }} />
              <col style={{ width: "4%" }} />
              <col style={{ width: "4.5%" }} />
              <col style={{ width: "4.5%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "5.5%" }} />
              <col style={{ width: "10.5%" }} />
              <col style={{ width: "8.5%" }} />
            </colgroup>
            <thead>
              <tr className="bg-[#fff4f6] text-left text-[12px] font-bold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-4">Image</th>
                <th className="px-4 py-4">Name</th>
                <th className="px-4 py-4">SKU</th>
                <th className="px-4 py-4">Barcode</th>
                <th className="px-4 py-4">Category</th>
                <th className="px-4 py-4">Brand</th>
                <th className="px-4 py-4">Unit</th>
                <th className="px-4 py-4">Tax</th>
                <th className="px-4 py-4">Weight</th>
                <th className="px-4 py-4">MRP</th>
                <th className="px-4 py-4">Selling Price</th>
                {/* <th className="px-4 py-4">Purchase Price</th> */}
                <th className="px-4 py-4">Status</th>
                <th className="px-4 py-4">Active</th>
                <th className="px-4 py-4">Actions</th>
              </tr>
            </thead>

            <tbody>
              {apiState.loading ? (
                <tr>
                  <td colSpan={14} className="px-4 py-12 text-center text-[13px] text-slate-500">
                    Loading products...
                  </td>
                </tr>
              ) : mapped.length === 0 ? (
                <tr>
                  <td colSpan={14} className="px-4 py-12 text-center text-[13px] text-slate-500">
                    No products found.
                  </td>
                </tr>
              ) : (
                mapped.map((p) => {
                  const switching = mutating.activeIds.has(p.id);

                  return (
                    <tr
                      key={p.id}
                      className="border-t border-slate-100 text-[13px] text-slate-600 transition hover:bg-[#fffafb]"
                    >
                      <td className="px-4 py-4">
                        <button
                          type="button"
                          onClick={() => openProductDetails(p.id)}
                          className="overflow-hidden rounded-[10px] border border-slate-200 bg-slate-50"
                        >
                          {p.image ? (
                            <img
                              src={p.image}
                              alt={p.name}
                              className="h-[54px] w-[54px] object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="flex h-[54px] w-[54px] items-center justify-center text-slate-300">
                              <Package size={22} />
                            </div>
                          )}
                        </button>
                      </td>

                      <td className="px-4 py-4">
                        <button
                          type="button"
                          onClick={() => openProductDetails(p.id)}
                          title={p.name}
                          className="line-clamp-2 w-full whitespace-normal break-words text-left font-semibold leading-[1.35] text-slate-700 transition hover:text-[#d86a78]"
                        >
                          {p.name}
                        </button>
                      </td>

                      <td className="px-4 py-4" title={p.sku}>
                        <span className="line-clamp-2 whitespace-normal break-words leading-[1.35]">{p.sku}</span>
                      </td>
                      <td className="px-4 py-4" title={p.barcode}>
                        <span className="block overflow-hidden text-ellipsis">{p.barcode}</span>
                      </td>
                      <td className="px-4 py-4" title={p.category}>
                        <span className="block overflow-hidden text-ellipsis">{p.category}</span>
                      </td>
                      <td className="px-4 py-4" title={p.brand}>
                        <span className="block overflow-hidden text-ellipsis">{p.brand}</span>
                      </td>
                      <td className="px-4 py-4">{p.unit}</td>
                      <td className="px-4 py-4">{p.tax}</td>
                      <td className="px-4 py-4">{p.weight}</td>
                      <td className="px-4 py-4">{p.mrp}</td>
                      <td className="px-4 py-4">{p.sellingPrice}</td>
                      {/* <td className="px-4 py-4 font-semibold text-slate-700">
                        {p.purchasePrice}
                      </td> */}
                      <td className="px-4 py-4">
                        <StatusPill value={p.status} />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <ActiveDot active={p.active} />
                          <Switch
                            checked={Boolean(p.active)}
                            disabled={switching}
                            onChange={(nextValue) => handleToggleActive(p, nextValue)}
                          />
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openProductEdit(p.id)}
                            className="rounded-[10px] border border-[#f2c6cc] p-2 text-[#d86a78] transition hover:bg-[#fff4f6]"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>

                          <button
                            type="button"
                            onClick={() => askDelete(p)}
                            className="rounded-[10px] border border-rose-200 p-2 text-rose-700 transition hover:bg-rose-50"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-5 space-y-4 2xl:hidden">
        {apiState.loading ? (
          <div className="rounded-[18px] border border-[#f2c6cc] bg-white px-4 py-10 text-center text-[13px] text-slate-500">
            Loading products...
          </div>
        ) : mapped.length === 0 ? (
          <div className="rounded-[18px] border border-[#f2c6cc] bg-white px-4 py-10 text-center text-[13px] text-slate-500">
            No products found.
          </div>
        ) : (
          mapped.map((p) => {
            const switching = mutating.activeIds.has(p.id);
            return (
              <ProductCard
                key={p.id}
                product={p}
                switching={switching}
                onView={() => openProductDetails(p.id)}
                onEdit={() => openProductEdit(p.id)}
                onDelete={() => askDelete(p)}
                onToggle={(nextValue) => handleToggleActive(p, nextValue)}
              />
            );
          })
        )}
      </div>

      <div className="mt-5 flex flex-col gap-3 rounded-[18px] border border-[#f2c6cc] bg-white px-4 py-4 text-[12px] text-slate-500 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-center sm:text-left">
          Showing {startIndex}-{endIndex} of {total}
        </div>

        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center justify-between gap-3 sm:justify-start">
            <span>Rows</span>
            <select
              value={pageSize}
              onChange={(e) => onChangePageSize(e.target.value)}
              className="h-9 min-w-[88px] rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[12px] text-slate-600 outline-none focus:border-[#d86a78]"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between gap-2 sm:justify-start">
            <button
              type="button"
              onClick={() => canPrev && setPage((p) => p - 1)}
              disabled={!canPrev}
              className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 font-semibold text-slate-600 disabled:opacity-50"
            >
              Prev
            </button>

            <span className="min-w-[86px] text-center">
              Page {page} / {totalPages}
            </span>

            <button
              type="button"
              onClick={() => canNext && setPage((p) => p + 1)}
              disabled={!canNext}
              className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 font-semibold text-slate-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ImportProductsModal
        open={importModal}
        onClose={() => setImportModal(false)}
        onImported={async () => {
          setImportModal(false);
          setPage(1);
          await loadProducts(1, pageSize, q, { status, category, subcategory, brand });
        }}
      />

      <CatalogManagerDrawer open={openCatalog} onClose={() => setOpenCatalog(false)} />
    </div>
  );
}
